import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Camera, CameraOff, Search, Dumbbell, GraduationCap, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, Card, Button, Loading, EmptyState, SeverityTag, TierBadge } from "@/components/kit";

function parseQr(text) {
  if (!text) return null;
  if (!text.startsWith("HSC-")) return null;
  const rest = text.slice(4);
  const last = rest.lastIndexOf("-");
  return last > 0 ? rest.slice(0, last) : rest;
}

export default function CheckIn() {
  const [scanning, setScanning] = useState(false);
  const [permError, setPermError] = useState(false);
  const [options, setOptions] = useState(null);
  const [loadingOpt, setLoadingOpt] = useState(false);
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const scannerRef = useRef(null);

  useEffect(() => {
    api.ownerMembers().then(setMembers).catch(() => {});
    return () => { stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOptions = async (memberId) => {
    setLoadingOpt(true);
    try { setOptions(await api.checkinOptions(memberId)); }
    catch (e) { toast.error(e.message); setOptions(null); }
    finally { setLoadingOpt(false); }
  };

  const onScan = async (text) => {
    const mid = parseQr(text);
    if (!mid) { toast.error("QR bukan kartu member HSC"); return; }
    await stop();
    loadOptions(mid);
  };

  const start = async () => {
    setPermError(false);
    setScanning(true);
    try {
      const el = document.getElementById("qr-reader");
      if (!el) return;
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;
      await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 240 }, onScan, () => {});
    } catch (e) {
      setScanning(false);
      setPermError(true);
    }
  };

  const stop = async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    setScanning(false);
    if (s) { try { await s.stop(); s.clear(); } catch {} }
  };

  const doCheckin = async (payload, label) => {
    try {
      await api.createCheckin(payload);
      toast.success(`Check-in ${label} berhasil`);
      loadOptions(options.member.id);
    } catch (e) { toast.error(e.message); }
  };

  const filtered = q.trim() ? members.filter((m) => (m.name || "").toLowerCase().includes(q.toLowerCase())).slice(0, 8) : [];

  return (
    <div>
      <PageHeader eyebrow="Check-In" title="Scan Member"
        subtitle="Pindai QR kartu member lewat webcam, atau cari manual untuk check-in gym / kelas." />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <div className="mb-4 hsc-label">Kamera</div>
          <div id="qr-reader" className="mx-auto mb-4 w-full max-w-xs overflow-hidden rounded-2xl border border-[#1F1F22] bg-black" />
          {permError && (
            <div className="mb-3 rounded-lg border border-[#3A3A3C] bg-[#160b0b] p-3 text-sm text-[#FF8080]">
              Akses kamera ditolak. Izinkan kamera di pengaturan browser lalu coba lagi, atau gunakan pencarian manual.
            </div>
          )}
          {!scanning ? (
            <Button className="w-full" onClick={start} data-testid="start-scan"><Camera className="h-4 w-4" /> Mulai Scan</Button>
          ) : (
            <Button variant="outline" className="w-full" onClick={stop} data-testid="stop-scan"><CameraOff className="h-4 w-4" /> Stop</Button>
          )}

          <div className="mt-6">
            <div className="hsc-label mb-2">Atau cari manual</div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5A5A5F]" />
              <input className="hsc-input pl-9" placeholder="Cari nama member" value={q} onChange={(e) => setQ(e.target.value)} data-testid="checkin-search" />
            </div>
            {filtered.map((m) => (
              <button key={m.id} onClick={() => { setQ(""); loadOptions(m.id); }}
                className="mt-1 flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-[#141416]" data-testid="checkin-member-option">
                <span className="text-white">{m.name}</span><TierBadge tier={m.tier} />
              </button>
            ))}
          </div>
        </Card>

        <Card>
          <div className="mb-4 hsc-label">Detail Check-In</div>
          {loadingOpt ? <Loading /> : !options ? (
            <EmptyState icon={Camera} title="Belum ada member dipilih" subtitle="Scan QR atau cari manual untuk mulai." />
          ) : (
            <div className="space-y-5">
              <div className="flex items-center justify-between rounded-xl border border-[#1F1F22] bg-[#0E0E10] p-4">
                <div className="font-display text-lg text-white">{options.member.name}</div>
                <TierBadge tier={options.member.tier} />
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm text-white"><Dumbbell className="mr-2 inline h-4 w-4 text-[#8E8E93]" /> Gym</span>
                  <SeverityTag value={options.gym.status === "active" ? "PAID" : options.gym.status === "expired" ? "expired" : "INFO"} />
                </div>
                <Button className="w-full" disabled={options.gym.status !== "active"}
                  onClick={() => doCheckin({ member_id: options.member.id, checkin_type: "gym" }, "Gym")} data-testid="checkin-gym">
                  <Check className="h-4 w-4" /> Check-in Gym {options.gym.days_left ? `(${options.gym.days_left}h)` : ""}
                </Button>
              </div>

              <div>
                <div className="mb-2 text-sm text-white"><GraduationCap className="mr-2 inline h-4 w-4 text-[#8E8E93]" /> Kelas Hari Ini</div>
                {!options.classes.length ? <div className="text-sm text-[#6E6E73]">Tidak ada kelas hari ini.</div> : (
                  <div className="space-y-2">
                    {options.classes.map((c) => (
                      <div key={c.enrollment_id} className="flex items-center justify-between rounded-lg border border-[#1F1F22] px-3 py-2 text-sm">
                        <div><div className="text-white">{c.class_name}</div><div className="text-[11px] text-[#6E6E73]">{c.time}</div></div>
                        {c.checked_in ? <SeverityTag value="PAID" /> : (
                          <Button variant="outline" onClick={() => doCheckin({ member_id: options.member.id, checkin_type: "class", class_name: c.class_name, time: c.time }, c.class_name)} data-testid="checkin-class">
                            Check-in
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
